/**
 * ============================================================================
 * MemberDetailModal - Member profile with history and renewal
 * ============================================================================
 *
 * Centered modal showing:
 * - Member profile info
 * - Current membership status
 * - Membership history timeline
 * - Renew button with inline form
 *
 * Mobile responsive: Full screen on mobile, centered modal on desktop
 *
 * ============================================================================
 */

import { CURRENCY_SYMBOL } from '../../utils/currency';
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import Modal from '../ui/Modal';
import {
  X,
  User,
  Mail,
  Phone,
  Calendar,
  Clock,
  CreditCard,
  History,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  AlertCircle,
  Smartphone,
  UserPlus,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { vendorApi } from '../../services/api';
import type { MemberDetailData, RenewalFormData, CancellationFormData } from '../../types/memberships';

interface PackageOption {
  id: number;
  package_name: string;
  price: number;
  duration: number;
  duration_type: 'day' | 'week' | 'month' | 'year';
  duration_months: number;
}

interface MemberDetailDrawerProps {
  isOpen: boolean;
  userId: number | null;
  venueSlug: string;
  packages: PackageOption[];
  onClose: () => void;
  onRenew: (userId: number, formData: RenewalFormData) => Promise<void>;
  isRenewing: boolean;
  onCancel?: (membershipId: number, formData: CancellationFormData) => Promise<void>;
  isCancelling?: boolean;
}

const INITIAL_RENEWAL_FORM: RenewalFormData = {
  package_id: '',
  payment_method: 'cash',
  price: '',
  start_date: new Date().toISOString().split('T')[0], // Default to today
  notes: '',
};

const INITIAL_CANCELLATION_FORM: CancellationFormData = {
  reason: '',
  refund_type: 'none',
  refund_amount: undefined,
};

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-NG', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatCurrency(amount: number): string {
  return CURRENCY_SYMBOL + amount.toLocaleString('en-NG');
}

function getStatusBadge(membership: { is_active: boolean; is_expired: boolean; is_expiring_soon: boolean }) {
  if (membership.is_expiring_soon) {
    return { label: 'Expiring Soon', className: 'bg-amber-100 text-amber-700' };
  }
  if (membership.is_expired) {
    return { label: 'Expired', className: 'bg-red-100 text-red-700' };
  }
  if (membership.is_active) {
    return { label: 'Active', className: 'bg-green-100 text-green-700' };
  }
  return { label: 'Inactive', className: 'bg-gray-100 text-gray-600' };
}

export function MemberDetailDrawer({
  isOpen,
  userId,
  venueSlug,
  packages,
  onClose,
  onRenew,
  isRenewing,
  onCancel,
  isCancelling = false,
}: MemberDetailDrawerProps) {
  const [showRenewalForm, setShowRenewalForm] = useState(false);
  const [renewalForm, setRenewalForm] = useState<RenewalFormData>(INITIAL_RENEWAL_FORM);
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [cancellingMembershipId, setCancellingMembershipId] = useState<number | null>(null);
  const [cancelForm, setCancelForm] = useState<CancellationFormData>(INITIAL_CANCELLATION_FORM);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setShowRenewalForm(false);
      setRenewalForm(INITIAL_RENEWAL_FORM);
      setShowFullHistory(false);
      setCancellingMembershipId(null);
      setCancelForm(INITIAL_CANCELLATION_FORM);
    }
  }, [isOpen, userId]);

  // Fetch member detail
  const {
    data: memberData,
    isLoading,
    error,
  } = useQuery<MemberDetailData>({
    queryKey: ['member-detail', venueSlug, userId],
    queryFn: () => vendorApi.getMemberDetail(venueSlug, userId!),
    enabled: isOpen && !!userId && !!venueSlug,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Auto-fill price when package changes
  useEffect(() => {
    if (renewalForm.package_id) {
      const pkg = packages.find((p) => p.id === Number(renewalForm.package_id));
      if (pkg) {
        setRenewalForm((prev) => ({ ...prev, price: pkg.price.toString() }));
      }
    }
  }, [renewalForm.package_id, packages]);

  const handleRenewalSubmit = async () => {
    if (!userId || !renewalForm.package_id) return;
    await onRenew(userId, renewalForm);
    setShowRenewalForm(false);
    setRenewalForm(INITIAL_RENEWAL_FORM);
  };

  const handleCancelSubmit = async () => {
    if (!cancellingMembershipId || !cancelForm.reason.trim() || !onCancel) return;
    await onCancel(cancellingMembershipId, cancelForm);
    setCancellingMembershipId(null);
    setCancelForm(INITIAL_CANCELLATION_FORM);
  };

  const selectedPkg = packages.find((p) => p.id === Number(renewalForm.package_id));
  const historyToShow = showFullHistory ? memberData?.history : memberData?.history?.slice(0, 3);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" preventClose={isRenewing || isCancelling} animate={false}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between z-10 flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Member Details</h2>
            <p className="text-xs text-gray-500">View profile and membership history</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 px-6">
              <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
              <p className="text-gray-600 text-center">
                {error instanceof Error ? error.message : 'Failed to load member details'}
              </p>
            </div>
          ) : memberData ? (
            <div className="p-5 space-y-5">
              {/* Profile Card */}
              <div className="bg-gray-50 rounded-xl p-4 sm:p-5">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-6 h-6 sm:w-7 sm:h-7 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                      {memberData.member.name}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-1">
                      {memberData.member.source === 'spotts' ? (
                        <Smartphone className="w-3.5 h-3.5 text-blue-500" />
                      ) : (
                        <UserPlus className="w-3.5 h-3.5 text-gray-400" />
                      )}
                      <span className="text-xs text-gray-500">
                        {memberData.member.source === 'spotts' ? 'Spotts User' : 'Manual Entry'}
                      </span>
                    </div>

                    {memberData.member.email && (
                      <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
                        <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{memberData.member.email}</span>
                      </div>
                    )}
                    {memberData.member.phone && (
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                        <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span>{memberData.member.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                      <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span>Member since {formatDate(memberData.member.member_since)}</span>
                    </div>
                  </div>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-4 pt-4 border-t border-gray-200">
                  <div className="text-center">
                    <p className="text-lg sm:text-xl font-bold text-gray-900">{memberData.stats.total_memberships}</p>
                    <p className="text-xs text-gray-500">Memberships</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg sm:text-xl font-bold text-gray-900">{formatCurrency(memberData.stats.total_spent)}</p>
                    <p className="text-xs text-gray-500">Total Spent</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg sm:text-xl font-bold text-gray-900">{memberData.stats.total_check_ins}</p>
                    <p className="text-xs text-gray-500">Check-ins</p>
                  </div>
                </div>
              </div>

              {/* Active Memberships (can have multiple concurrent) */}
              {memberData.active_memberships.length > 0 ? (
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900 text-sm sm:text-base">
                    Active Memberships {memberData.active_memberships.length > 1 && `(${memberData.active_memberships.length})`}
                  </h4>

                  {memberData.active_memberships.map((membership) => (
                    <div key={membership.id} className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5">
                      <div className="flex items-center justify-between mb-3 sm:mb-4">
                        <h5 className="font-semibold text-gray-900 text-sm">{membership.package_name}</h5>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                            getStatusBadge(membership).className
                          }`}
                        >
                          {getStatusBadge(membership).label}
                        </span>
                      </div>

                      <div className="space-y-2 sm:space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-xs sm:text-sm text-gray-500">Membership ID</span>
                          <span className="text-xs sm:text-sm font-mono text-gray-900 font-semibold">
                            {membership.payment_reference}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs sm:text-sm text-gray-500">Period</span>
                          <span className="text-xs sm:text-sm text-gray-700 text-right">
                            {formatDate(membership.start_date)} – {formatDate(membership.end_date)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs sm:text-sm text-gray-500">Days Remaining</span>
                          <span
                            className={`text-xs sm:text-sm font-semibold ${
                              membership.days_remaining <= 3
                                ? 'text-amber-600'
                                : membership.is_expired
                                ? 'text-red-500'
                                : 'text-gray-900'
                            }`}
                          >
                            {membership.is_expired ? 'Expired' : `${membership.days_remaining} days`}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs sm:text-sm text-gray-500">Check-ins</span>
                          <span className="text-xs sm:text-sm text-gray-700">{membership.total_check_ins}</span>
                        </div>
                        {membership.last_check_in_at && (
                          <div className="flex justify-between items-center">
                            <span className="text-xs sm:text-sm text-gray-500">Last Check-in</span>
                            <span className="text-xs sm:text-sm text-gray-700">
                              {formatDate(membership.last_check_in_at)}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Cancel Membership Option */}
                      {membership.is_active && !membership.is_expired && onCancel && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          {cancellingMembershipId !== membership.id ? (
                            <button
                              onClick={() => {
                                setCancellingMembershipId(membership.id);
                                setShowRenewalForm(false);
                                setCancelForm(INITIAL_CANCELLATION_FORM);
                              }}
                              className="text-xs text-red-600 hover:text-red-800 font-medium flex items-center gap-1"
                            >
                              <XCircle className="w-3.5 h-3.5" />
                              Cancel this membership
                            </button>
                          ) : (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
                              <div className="flex items-start gap-2">
                                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                <div>
                                  <h5 className="font-semibold text-red-800 text-sm">Cancel Membership</h5>
                                  <p className="text-xs text-red-600 mt-1">
                                    This action cannot be undone. The member will lose access immediately.
                                  </p>
                                </div>
                              </div>

                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">
                                  Reason for cancellation <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                  value={cancelForm.reason}
                                  onChange={(e) => setCancelForm((prev) => ({ ...prev, reason: e.target.value }))}
                                  placeholder="Enter the reason for cancellation..."
                                  rows={2}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-red-400 resize-none"
                                />
                              </div>

                              {/* Refund Tracking */}
                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-2">
                                  Refund Status <span className="text-red-500">*</span>
                                </label>
                                <div className="space-y-2">
                                  {(['none', 'partial', 'full'] as const).map((type) => (
                                    <label key={type} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name={`refund_type_${membership.id}`}
                                        value={type}
                                        checked={cancelForm.refund_type === type}
                                        onChange={(e) => setCancelForm((prev) => ({
                                          ...prev,
                                          refund_type: e.target.value as 'none' | 'partial' | 'full',
                                          refund_amount: e.target.value === 'partial' ? prev.refund_amount : undefined,
                                        }))}
                                        className="w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
                                      />
                                      <span className="text-xs text-gray-700">
                                        {type === 'none' && 'No refund (keep 100% earnings)'}
                                        {type === 'partial' && 'Partial refund (specify amount below)'}
                                        {type === 'full' && 'Full refund (return all earnings)'}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              </div>

                              {/* Partial Refund Amount Input */}
                              {cancelForm.refund_type === 'partial' && (() => {
                                const maxRefund = Number(membership.price) || 0;
                                const amount = cancelForm.refund_amount ?? 0;
                                const isInvalid = amount <= 0 || amount > maxRefund;
                                const errorMsg = amount > maxRefund
                                  ? `Refund cannot exceed ${CURRENCY_SYMBOL}${maxRefund.toLocaleString()}`
                                  : amount < 0
                                    ? 'Refund amount cannot be negative'
                                    : null;
                                return (
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                                      Refund Amount ({CURRENCY_SYMBOL}) <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                      type="number"
                                      value={cancelForm.refund_amount || ''}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setCancelForm((prev) => ({
                                          ...prev,
                                          refund_amount: val ? Math.max(0, Number(val)) : undefined,
                                        }));
                                      }}
                                      placeholder="Enter refund amount..."
                                      min="1"
                                      max={maxRefund}
                                      step="0.01"
                                      className={`w-full px-3 py-2 border rounded-lg text-sm outline-none ${
                                        errorMsg
                                          ? 'border-red-400 bg-red-50 focus:border-red-500'
                                          : 'border-gray-200 focus:border-red-400'
                                      }`}
                                    />
                                    {errorMsg ? (
                                      <p className="text-xs text-red-600 font-medium mt-1">
                                        {errorMsg}
                                      </p>
                                    ) : (
                                      <p className="text-xs text-gray-500 mt-1">
                                        Max refund: {CURRENCY_SYMBOL}{maxRefund.toLocaleString()}
                                      </p>
                                    )}
                                  </div>
                                );
                              })()}

                              <div className="flex gap-2 pt-2">
                                <button
                                  onClick={() => {
                                    setCancellingMembershipId(null);
                                    setCancelForm(INITIAL_CANCELLATION_FORM);
                                  }}
                                  className="flex-1 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                  Keep Active
                                </button>
                                <button
                                  onClick={handleCancelSubmit}
                                  disabled={
                                    !cancelForm.reason.trim() ||
                                    isCancelling ||
                                    (cancelForm.refund_type === 'partial' && (!cancelForm.refund_amount || cancelForm.refund_amount <= 0 || cancelForm.refund_amount > (membership.price || 0)))
                                  }
                                  className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                  {isCancelling ? (
                                    <>
                                      <RefreshCw className="w-4 h-4 animate-spin" />
                                      Cancelling...
                                    </>
                                  ) : (
                                    <>
                                      <XCircle className="w-4 h-4" />
                                      Cancel Membership
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-amber-900 text-sm sm:text-base mb-1">No Active Membership</h4>
                      <p className="text-xs sm:text-sm text-amber-700">
                        This member doesn't have an active membership. View history below or renew their membership.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Renewal Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-5">
                {!showRenewalForm ? (
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <h4 className="font-semibold text-blue-900 text-sm sm:text-base">Renew Membership</h4>
                      <p className="text-xs text-blue-600 mt-0.5 truncate">
                        {memberData.active_memberships.length > 0
                          ? 'Add another membership'
                          : 'Starts today'}
                      </p>
                    </div>
                    <button
                      onClick={() => setShowRenewalForm(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors flex-shrink-0"
                    >
                      Renew
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-blue-900 text-sm sm:text-base">Renewal Details</h4>
                      <button
                        onClick={() => setShowRenewalForm(false)}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        Cancel
                      </button>
                    </div>

                    {/* Package Selection */}
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                        Package <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={renewalForm.package_id}
                        onChange={(e) =>
                          setRenewalForm((prev) => ({
                            ...prev,
                            package_id: e.target.value ? Number(e.target.value) : '',
                          }))
                        }
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-500 bg-white"
                      >
                        <option value="">Select a package</option>
                        {packages.map((pkg) => (
                          <option key={pkg.id} value={pkg.id}>
                            {pkg.package_name} — {formatCurrency(pkg.price)} ({pkg.duration} {pkg.duration === 1 ? pkg.duration_type : `${pkg.duration_type}s`})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Payment Method */}
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">Payment Method</label>
                      <div className="flex gap-2">
                        {(['cash', 'transfer', 'pos'] as const).map((method) => (
                          <button
                            key={method}
                            type="button"
                            onClick={() => setRenewalForm((prev) => ({ ...prev, payment_method: method }))}
                            className={`flex-1 py-2 rounded-lg font-semibold text-xs sm:text-sm transition-all border ${
                              renewalForm.payment_method === method
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white text-gray-700 border-gray-200 hover:border-blue-400'
                            }`}
                          >
                            {method === 'pos' ? 'POS' : method.charAt(0).toUpperCase() + method.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Price */}
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">Amount ({CURRENCY_SYMBOL})</label>
                      <div className="relative">
                        <CreditCard className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                          type="number"
                          value={renewalForm.price}
                          onChange={(e) => setRenewalForm((prev) => ({ ...prev, price: e.target.value }))}
                          placeholder="Auto-filled from package"
                          min="0"
                          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {/* Start Date */}
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">Start Date</label>
                      <div className="relative">
                        <Calendar className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                          type="date"
                          value={renewalForm.start_date || ''}
                          onChange={(e) => setRenewalForm((prev) => ({ ...prev, start_date: e.target.value }))}
                          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-500"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Leave as today or set a future start date
                      </p>
                    </div>

                    {/* Duration Preview */}
                    {selectedPkg && renewalForm.start_date && (
                      <div className="flex justify-between items-center bg-blue-100 rounded-lg p-3 text-xs sm:text-sm">
                        <span className="text-blue-700">Membership period</span>
                        <span className="font-semibold text-blue-900">
                          {(() => {
                            const startDate = new Date(renewalForm.start_date);
                            const endDate = new Date(startDate);
                            switch (selectedPkg.duration_type) {
                              case 'day':
                                endDate.setDate(endDate.getDate() + selectedPkg.duration);
                                break;
                              case 'week':
                                endDate.setDate(endDate.getDate() + selectedPkg.duration * 7);
                                break;
                              case 'year':
                                endDate.setFullYear(endDate.getFullYear() + selectedPkg.duration);
                                break;
                              default: // 'month'
                                endDate.setMonth(endDate.getMonth() + selectedPkg.duration);
                                break;
                            }
                            return `${startDate.toLocaleDateString('en-NG', { day: 'numeric', month: 'short' })} – ${endDate.toLocaleDateString('en-NG', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}`;
                          })()}
                        </span>
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      onClick={handleRenewalSubmit}
                      disabled={!renewalForm.package_id || isRenewing}
                      className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold text-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isRenewing ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Renewing...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Confirm Renewal
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              {/* Membership History */}
              {memberData.history && memberData.history.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <History className="w-4 h-4 text-gray-400" />
                    <h4 className="font-semibold text-gray-900 text-sm sm:text-base">Membership History</h4>
                  </div>

                  <div className="space-y-3">
                    {historyToShow?.map((item, index) => (
                      <div
                        key={item.id}
                        className={`border rounded-lg p-3 sm:p-4 ${
                          index === 0 ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2 gap-2">
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 text-sm truncate">{item.package_name}</p>
                            <p className="text-xs text-gray-500">{item.plan_type}</p>
                            <p className="text-xs font-mono text-gray-400 mt-0.5">{item.payment_reference}</p>
                          </div>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${
                              item.cancellation_reason
                                ? 'bg-red-100 text-red-700'
                                : item.is_active
                                ? 'bg-green-100 text-green-700'
                                : item.is_expired
                                ? 'bg-gray-100 text-gray-600'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {item.cancellation_reason ? 'Canceled' : item.is_active ? 'Active' : 'Completed'}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(item.start_date)} – {formatDate(item.end_date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {item.total_check_ins} check-ins
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <p className="text-sm font-semibold text-gray-700">{formatCurrency(item.price)}</p>
                          {item.refund_amount && item.refund_amount > 0 && (
                            <span className="text-xs text-red-600 font-medium">
                              Refunded: {formatCurrency(item.refund_amount)}
                            </span>
                          )}
                        </div>
                        {item.cancellation_reason && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <p className="text-xs text-gray-600">
                              <span className="font-semibold">Reason:</span> {item.cancellation_reason}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {memberData.history.length > 3 && (
                    <button
                      onClick={() => setShowFullHistory(!showFullHistory)}
                      className="w-full mt-3 py-2 text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center justify-center gap-1"
                    >
                      {showFullHistory ? (
                        <>
                          <ChevronUp className="w-4 h-4" />
                          Show Less
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          Show All ({memberData.history.length} memberships)
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
    </Modal>
  );
}
